import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { useAccountsQuery } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { toTitleCase, type Maybe } from '@vrplatform/utils';
import { useMemo } from 'react';
import type { BaseCustomFilterProps } from './_types';

type AccountValue = 'excluded' | 'unmapped' | (string & {});

type Single = {
  type: 'single';
  account: Maybe<AccountValue>;
  setAccount: (account: AccountValue | null) => void;
};

type Multiple = {
  type: 'multiple';
  account: Maybe<AccountValue[]>;
  setAccount: (accounts: AccountValue[] | null) => void;
};

type Props = BaseCustomFilterProps & (Single | Multiple);

function useAccountOptions({ skip }: { skip: boolean }) {
  const queryData = useAccountsQuery({
    skip,
    status: 'active',
  });

  const data = useMemo<SelectItem[]>(() => {
    return (
      queryData.data?.map((entry) => ({
        value: entry.id,
        label: entry.name || 'No name',
        group: toTitleCase(entry.category?.classification || '-'),
      })) ?? []
    );
  }, [queryData.data]);

  return {
    ...queryData,
    data,
  };
}

function useCustomActions(setFilter: (id: AccountValue) => void) {
  return useMemo(
    () => [
      {
        label: 'Excluded',
        id: 'exclude',
        icon: <Icon icon="CrossCircleIcon" size={16} />,
        onSubmit: () => setFilter('excluded'),
      },
      {
        label: 'Unmapped',
        id: 'unmapped',
        icon: <Icon icon="AlertCircleIcon" size={16} />,
        onSubmit: () => setFilter('unmapped'),
      },
    ],
    [setFilter]
  );
}

export const AccountFilter = ({ label = 'Account', ...props }: Props) => {
  if (props.type === 'single') {
    return <SingleInput {...props} label={label} />;
  }

  return <MultipleInput {...props} label={label} />;
};

type Base = {
  label: string;
};

const SingleInput = ({ account, setAccount, label }: Base & Single) => {
  const gate = useSelectQueryGate({ value: account });
  const queryData = useAccountOptions({
    skip: !gate.shouldFetchAny,
  });
  const options = queryData.data || [];

  const value = account
    ? getSimpleAccountValue(account) ||
      queryData.data?.find((entry) => entry.value === account) ||
      null
    : null;
  const customActions = useCustomActions(setAccount);

  return (
    <Filter.Select
      type="single"
      label={label}
      withinPortal
      value={value}
      data={{
        options,
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      setValue={(value) => setAccount(value?.value || null)}
      customBottomActions={customActions}
      onOpenChange={gate.onOpenChange}
    />
  );
};

const getSimpleAccountValue = (
  account: AccountValue | undefined
): SelectItem | null => {
  if (account === 'excluded') return { value: 'excluded', label: 'Excluded' };
  if (account === 'unmapped') return { value: 'unmapped', label: 'Unmapped' };
  return null;
};

const MultipleInput = ({ account, setAccount, label }: Base & Multiple) => {
  const gate = useSelectQueryGate({ value: account });
  const queryData = useAccountOptions({ skip: !gate.shouldFetchAny });
  const options = queryData.data || [];
  const simpleAccountValue = getSimpleAccountValue(account?.[0]);
  const value = simpleAccountValue
    ? [simpleAccountValue]
    : options.filter((entry) => account?.includes(entry.value));
  const customActions = useCustomActions((id) => setAccount(id ? [id] : null));

  return (
    <Filter.Select
      type="multiple"
      withinPortal
      label={label}
      value={value}
      data={{
        options,
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      setValue={(value) =>
        setAccount(value.length ? value.map((entry) => entry.value) : null)
      }
      customBottomActions={customActions}
      onOpenChange={gate.onOpenChange}
    />
  );
};
